import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  DataSurface,
  PageShell,
  cn,
} from "../design-system";
import {
  agentSetupClients,
  MCP_PROTOCOL_VERSION,
  type AgentSetupClient,
  type AgentStepPart,
} from "../site/agentsContent";
import "./AgentsPage.css";

interface CopyButtonProps {
  value: string;
  label: string;
  className?: string;
  children?: React.ReactNode;
}


function StepBody({ parts }: { parts: readonly AgentStepPart[] }): React.ReactElement {
  return <>{parts.map((part, index) => {
    if (typeof part === "string") return <React.Fragment key={index}>{part}</React.Fragment>;
    if (part.kind === "strong") return <strong key={index}>{part.text}</strong>;
    return <code key={index}>{part.text}</code>;
  })}</>;
}

function CopyButton({ value, label, className, children }: CopyButtonProps): React.ReactElement {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }
  }, []);

  const handleCopy = async (): Promise<void> => {
    if (!value || !window.navigator.clipboard) return;

    try {
      await window.navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
      return;
    }

    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <Button
      aria-label={label}
      className={cn("agents-copy-button", className)}
      onClick={() => { void handleCopy(); }}
      size="sm"
      type="button"
      variant="outline"
    >
      {copied ? "Copied" : children || "Copy"}
    </Button>
  );
}

function CopyField({ value, label }: CopyButtonProps): React.ReactElement {
  return (
    <div className="agents-copy-field">
      <code>{value}</code>
      <CopyButton value={value} label={label} />
    </div>
  );
}

function CodeBlock({ code, label }: { code: string; label: string }): React.ReactElement {
  return (
    <div className="agents-code-block" role="group" aria-label={`${label} snippet`}>
      <div className="agents-code-block__bar">
        <span>{label}</span>
        <CopyButton value={code} label={`Copy ${label}`} />
      </div>
      <pre><code>{code}</code></pre>
    </div>
  );
}

function ClientPanel({ client }: { client: AgentSetupClient }): React.ReactElement {
  return (
    <div
      className="agents-client-panel"
      id={`agents-client-${client.id}`}
      role="region"
      aria-label={`${client.name} setup`}
    >
      <div className="agents-client-panel__header">
        <div>
          <span>{client.context}</span>
          <h2>{client.name}</h2>
        </div>
      </div>

      <dl className="agents-client-summary">
        <div>
          <dt>Best for</dt>
          <dd>{client.bestFor}</dd>
        </div>
        <div>
          <dt>Authentication</dt>
          <dd>{client.auth}</dd>
        </div>
      </dl>

      {client.steps && (
        <ol className="agents-step-list">
          {client.steps.map((step) => (
            <li key={step.label}>
              <strong>{step.label}</strong>
              <span><StepBody parts={step.body} /></span>
            </li>
          ))}
        </ol>
      )}

      {client.copyValue && client.copyLabel && (
        <div className="agents-connection-block">
          <span>Server URL</span>
          <CopyField value={client.copyValue} label={client.copyLabel} />
        </div>
      )}

      {client.code && client.copyLabel && (
        <CodeBlock code={client.code} label={client.copyLabel.replace("Copy ", "")} />
      )}
    </div>
  );
}

function SetupSurface(): React.ReactElement {
  return (
    <section className="agents-section" aria-label="AI client setup">
      <div className="agents-section__header">
        <div>
          <span>Setup guides</span>
          <h2>Connect from the AI tool you already use</h2>
          <p>Every guide is visible on this page so people and AI assistants can read the complete setup without opening tabs.</p>
        </div>
        <nav className="agents-client-jump-nav" aria-label="Jump to client setup">
          {agentSetupClients.map((client) => (
            <a href={`#agents-client-${client.id}`} key={client.id}>{client.name}</a>
          ))}
        </nav>
      </div>
      <DataSurface className="agents-setup-surface">
        {/* Keep every client in the document. Tabs previously made only ChatGPT visible to crawlers and first-time visitors. */}
        {agentSetupClients.map((client) => (
          <ClientPanel client={client} key={client.id} />
        ))}
      </DataSurface>
    </section>
  );
}

export default function AgentsPage(): React.ReactElement {
  return (
    <PageShell className="agents-page" innerClassName="agents-page__inner">
      <header className="agents-page__header">
        <div>
          <h1>MCP for Agents</h1>
          <p>Use BotSpot from ChatGPT, Claude, Claude Code, Codex, Gemini CLI, Cursor, or another MCP-compatible client.</p>
          <p><strong>Wire contract:</strong> MCP {MCP_PROTOCOL_VERSION} over stateless HTTP POST. Current clients negotiate this automatically.</p>
        </div>
        <Button as={Link} to="/account-settings/api-keys" variant="outline">
          Create API key
        </Button>
      </header>
      <SetupSurface />
    </PageShell>
  );
}
